import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useStudentAuth } from '../../../context/StudentAuthContext';
import { toast } from 'react-hot-toast';

const StudentQuizIntro = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { studentApi } = useStudentAuth();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [hasInProgress, setHasInProgress] = useState(false);

  useEffect(() => {
    fetchQuizDetails();
  }, [quizId]);

  const fetchQuizDetails = async () => {
    try {
      setLoading(true);
      const response = await studentApi.get(`/student/quizzes/${quizId}`);
      setQuiz(response.data);
      setHasInProgress(response.data.has_in_progress);
    } catch (error) {
      toast.error('Failed to load quiz details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    if (!agreed) return;
    try {
      await studentApi.post(`/student/quizzes/${quizId}/start`);
      navigate(`/student/quiz/${quizId}/take`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start quiz');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Quiz not found</p>
        <Link to="/student/quizzes" className="text-blue-600 hover:underline mt-2 inline-block">
          Back to Quizzes
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-xl shadow border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h1 className="text-2xl font-bold text-gray-900">{quiz.title}</h1>
          <p className="text-gray-600 mt-2">{quiz.description}</p>
        </div>

        <div className="p-6 space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <p className="text-sm text-blue-800 font-medium">Questions</p>
              <p className="text-2xl font-bold text-blue-900">{quiz.questions_count || 0}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-sm text-green-800 font-medium">Attempts Allowed</p>
              <p className="text-2xl font-bold text-green-900">
                {quiz.allow_multiple_attempts ? 'Multiple' : 'Single'}
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 text-center">
              <p className="text-sm text-purple-800 font-medium">Attempts Made</p>
              <p className="text-2xl font-bold text-purple-900">{quiz.attempts_count || 0}</p>
            </div>
          </div>

          {/* In Progress Banner */}
          {hasInProgress && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                </svg>
                <div className="flex-1">
                  <p className="font-medium text-yellow-800">You have an in-progress attempt</p>
                  <p className="text-sm text-yellow-700">You can continue where you left off.</p>
                </div>
                <button
                  onClick={() => navigate(`/student/quiz/${quizId}/take`)}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Instructions</h3>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>You can answer questions in any order.</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>{quiz.show_answers_immediately ? 'Correct answers will be shown immediately after you select.' : 'Answers will be revealed after submission.'}</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>Your progress is saved automatically.</span>
              </li>
              {quiz.shuffle_questions && (
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  <span>Questions will be shuffled randomly for each attempt.</span>
                </li>
              )}
            </ul>
          </div>

          {/* Agreement */}
          <div className="border border-gray-200 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                I understand this is a practice quiz and I will answer honestly to improve my learning.
              </span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-100">
            <Link
              to="/student/quizzes"
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              Back
            </Link>
            <button
              onClick={handleStart}
              disabled={!agreed}
              className={`px-8 py-3 rounded-lg font-medium transition ${
                agreed
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {hasInProgress ? 'Continue Quiz' : 'Start Quiz'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentQuizIntro;